import { AgTechDataAction, DataActionResults, useDataActionExecutor } from "@agtech/core/data/actions/AgTechDataActions"
import { AgTechApiEntityResponse } from "@agtech/core/data/actions/AgTechHttpRequests"
import { useAgTechHttpPostRequest, useAgTechHttpPostAndConvertRequest, useAgTechHttpSubmitEntityAndConvertRequest } from "@agtech/core/data/http/AgTechApiPostRequests"
import { isLotInvoiced } from "../lots/LotData"
import { SaleBidder } from "./BidderData"
import { BidderDataGetSaleBiddersQuery, useBidderDataGetSaleBidderDataQuery } from "./BidderDataQueries"
import { useCurrentSale } from "../sales/SaleDataStore"
import { useAgTechPopUpContext } from "@agtech/core/functions/AgTechAppPopUps"
import { useAgTechQuery, waitOnQuery } from "@agtech/core/data/queries/AgTechDataQueries"
import { LotDataGetLotsQuery } from "../lots/LotDataQueries"
import { AgTechContact } from "@agtech/data/contacts/ContactData"
import { useContactDataWriteContactAction } from "@agtech/data/contacts/ContactDataActions"

/// Bidder Data DTOs

type BidderRegistrationDTO = {
    id: number,
    bidderNumber: string,
    contact: AgTechContact
}

type SaleBidderResolutionDTO = {
    saleId: number,
    bidderNumber?: string,
    bidderId?: number,
    contactId: number
}

export type BidderContactRegistration = AgTechContact & {
    bidderId: number,
    bidderNumber: string
}

/// Bidder Data Actions

export type BidderNumberUpdateProps = {
    originalBidderNumber: string,
    updatedBidderContact: SaleBidder
}

export const useBidderDataUpdateBidderRegistrationAction = (): AgTechDataAction<BidderRegistrationDTO, SaleBidder> => {
    const actionExecutor = useDataActionExecutor();

    const bidderRegistrationAction = useBidderDataRegisterBidderAction();
    const bidderNumberUpdateAction = useBidderDataUpdateBidderNumberAction();
    const bidderResolveAction = useBidderDataResolveBidderAction();
    const bidderRemovalAction = useBidderDataRemoveBidderAction();

    return {
        name: 'UpdateBidderRegistration',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating bidder...',
            actionConfirmationMessage: `Successfully updated bidder`,
            shouldLogExecution: false
        }),
        action: async props => {
            const originalBidder = props.originalEntity;
            const submittedBidder = props.submittedEntity;

            const wasBidderRegistered = submittedBidder.id === 0;
            const wasBidderResolved = submittedBidder.id !== 0 && originalBidder?.bidderNumber === submittedBidder.bidderNumber && originalBidder?.contact?.id !== submittedBidder.contact?.id;
            const wasBidderRemoved = originalBidder?.bidderNumber && !submittedBidder.bidderNumber;

            if (wasBidderRegistered) {
                const bidderRegistrationResult = await actionExecutor.executeAction(bidderRegistrationAction, {
                    submittedEntity: {
                        bidderNumber: submittedBidder.bidderNumber!,
                        contact: submittedBidder.contact
                    }
                });
    
                return bidderRegistrationResult.success
                    ? DataActionResults.Success({ ...props.submittedEntity, bidderNumber: bidderRegistrationResult.success.data.bidderNumber })
                    : DataActionResults.Failed('An error occurred while registering the bidder. Please try again.');
            }
            else if (wasBidderResolved) {
                const bidderIdAfterResolution = await actionExecutor.executeAction(bidderResolveAction, {
                    submittedEntity: submittedBidder
                });

                return bidderIdAfterResolution.success
                    ? DataActionResults.Success({ ...props.submittedEntity, id: bidderIdAfterResolution.success.data })
                    : DataActionResults.Failed('An error occurred while resolving the bidder. Please try again.');
            }
            else if (wasBidderRemoved) {
                const bidderRemovalResult = await actionExecutor.executeAction(bidderRemovalAction, {
                    submittedEntity: { bidderId: submittedBidder.id!, bidderNumber: submittedBidder.bidderNumber! },
                });

                return bidderRemovalResult.success
                    ? DataActionResults.Success(undefined)
                    : DataActionResults.Failed('An error occurred while removing the bidder. Please try again.');
            }
            else {
                return await actionExecutor.executeAction(bidderNumberUpdateAction, { ...props });
            }
        }
    }
}

const useBidderDataRegisterBidderAction = (): AgTechDataAction<{ bidderNumber: string, contact: AgTechContact }, SaleBidder> => {
    const currentSale = useCurrentSale();
    const bidderQuery = useBidderDataGetSaleBidderDataQuery();

    const registerBidderPostRequest = useAgTechHttpPostAndConvertRequest<{
        id: number,
        bidderNumber: string,
        contactId: number,
        saleId: number,
        date: Date
    }, AgTechApiEntityResponse<number>, SaleBidder>();

    return {
        name: 'RegisterBidder',
        getConfiguration: () => ({
            actionExecutionMessage: 'Registering bidder...',
            actionConfirmationMessage: `Successfully registered bidder`
        }),
        validate: async props  => {
            const submittedBidder = props.submittedEntity;
            const existingBidderData = await waitOnQuery(bidderQuery);
            const existingBidders = Object.values(existingBidderData.bidders);

            const existingBidderWithSameBidderNumber = existingBidders.find(bidder =>
                bidder.bidderNumber === submittedBidder.bidderNumber);

            if (existingBidderWithSameBidderNumber) {
                props.validation.failWithWarning(`A bidder with bidder number ${submittedBidder.bidderNumber} already exists in this sale. Please pick a different bidder number.`);
            }

            const existingBidderForSameContact = existingBidders.find(bidder => bidder.contact?.id === submittedBidder.contact.id);

            if (existingBidderForSameContact) {
                props.validation.failWithWarning(`The selected contact is already registered with bidder number: ${existingBidderForSameContact.bidderNumber}`);
            }
        },
        action: async props => {
            return await registerBidderPostRequest.post({
                path: `Bidder/Register/${currentSale.id}`,
                postedData: {
                    id: 0,
                    bidderNumber: props.submittedEntity.bidderNumber!,
                    contactId: props.submittedEntity.contact.id,
                    saleId: currentSale.id,
                    date: new Date()
                },
                convert: registrationResponse => ({
                    id: registrationResponse.data,
                    bidderNumber: props.submittedEntity.bidderNumber,
                    contactId: props.submittedEntity.contact.id
                })
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id },
                update: bidders => {
                    return BidderDataGetSaleBiddersQuery.actions.writeBidder(bidders, {
                        id: props.responseData.id,
                        bidderNumber: props.submittedEntity.bidderNumber,
                        contact: props.submittedEntity.contact
                    });
                }
            });
        }
    }
}

export const useBidderDataRegisterNewContactAction = (): AgTechDataAction<BidderContactRegistration, BidderContactRegistration> => {
    const actionExecutor = useDataActionExecutor();
    const writeContactAction = useContactDataWriteContactAction();
    const registerBidderAction = useBidderDataRegisterBidderAction();

    return {
        name: 'RegisterNewContact',
        getConfiguration: props => ({
            actionExecutionMessage: 'Registering contact...',
            actionConfirmationMessage: `Successfully registered ${props.submittedEntity.firstName} ${props.submittedEntity.lastName} with bidder number: ` + props.submittedEntity.bidderNumber,
        }),
        action: async props => {
            return await actionExecutor.executeStep(writeContactAction, {
                ...props,
                onSuccess: async createdContact => {
                    const contactBidderRegistrationResult = await actionExecutor.executeAction(registerBidderAction, {
                        submittedEntity: {
                            bidderNumber: props.submittedEntity.bidderNumber,
                            contact: createdContact
                        }
                    });

                    return contactBidderRegistrationResult.success
                        ? DataActionResults.Success({
                            ...createdContact,
                            bidderNumber: props.submittedEntity.bidderNumber,
                            bidderId: contactBidderRegistrationResult.success.data.id
                        })
                        : DataActionResults.Failed('An error occurred while registering the new contact. Please try again.');
                }
            });
        }
    }
}

export const useBidderDataResolveBidderAction = (): AgTechDataAction<BidderRegistrationDTO, number> => {
    const currentSale = useCurrentSale();

    return {
        name: 'ResolveBidder',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating bidder number...',
            actionConfirmationMessage: `Successfully updated bidder number`
        }),
        validate: async props  => {
            props.validation.failWithWarningIf(props.submittedEntity.id === undefined, "Please select a contact a register a new one");
        },
        action: async props => {
            return await props.http.post<SaleBidderResolutionDTO, AgTechApiEntityResponse<number>, number>({
                request: 'Bidder/Resolve',
                inputData: {
                    saleId: currentSale.id,
                    bidderNumber: props.submittedEntity.bidderNumber,
                    bidderId: props.submittedEntity.id,
                    contactId: props.submittedEntity.contact.id
                },
                convert: response => response.data
            });
        },
        onSuccess: async props => {
            const updatedBidder = {
                ...props.submittedEntity,
                id: props.responseData
            };

            props.updateQueryData({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id },
                update: bidders => {
                    return BidderDataGetSaleBiddersQuery.actions.writeBidder(bidders, updatedBidder);
                }
            });
        }
    }
}

const useBidderDataUpdateBidderNumberAction = (): AgTechDataAction<SaleBidder, SaleBidder> => {
    const currentSale = useCurrentSale();

    const biddersQuery = useAgTechQuery({
        query: BidderDataGetSaleBiddersQuery,
        queryContext: { saleId: currentSale.id }
    });

    const lotsQuery = useAgTechQuery({
        query: LotDataGetLotsQuery,
        queryContext: { saleId: currentSale.id }
    });

    const updatedBidderNumberSubmitRequest = useAgTechHttpSubmitEntityAndConvertRequest<SaleBidder, SaleBidder>();

    return {
        name: 'UpdateBidderNumber',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating bidder number...',
            actionConfirmationMessage: `Successfully updated bidder number`,
        }),
        validate: async props  => {
            const originalBidder = props.originalEntity;
            const submittedBidder = props.submittedEntity;

            if (originalBidder?.bidderNumber === submittedBidder.bidderNumber) {
                return;
            }

            const bidderData = await waitOnQuery(biddersQuery);
            const lotData = await waitOnQuery(lotsQuery);

            const existingBidderWithSameBidderNumber = Object.values(bidderData)
                .find(bidder => bidder.bidderNumber === submittedBidder.bidderNumber);

            props.validation.failWithWarningIf(
                existingBidderWithSameBidderNumber !== undefined,
                `A bidder with bidder number "${submittedBidder.bidderNumber}" already exists in this sale. Please specify a different bidder number`,
                () => props.originalEntity ? { ...props.originalEntity } : { ...props.submittedEntity });

            if (submittedBidder.bidderNumber === '') {
                const lotsAssociatedWithBidder = Object.values(lotData)
                    .filter(lt => lt.originalBidderId === submittedBidder.id);

                // TODO: Update the warning message here
                props.validation.failWithWarningIf(lotsAssociatedWithBidder.length > 0,
                {
                    header: `Bidder "${originalBidder?.bidderNumber ?? submittedBidder.bidderNumber}" has purchased ${lotsAssociatedWithBidder.length === 1 ? 'lot ' + lotsAssociatedWithBidder[0].lotNumber + '' : lotsAssociatedWithBidder.length + ' lots'}
                                in this sale. To remove this bidder, please remove bidder information from ${lotsAssociatedWithBidder.length === 1 ? 'that lot' : 'those lots'} first`,
                    details: 'If these lots have been invoiced, this bidder cannot be removed from this sale'
                });
            }
        },
        confirm: async props => {
            const originalBidder = props.originalEntity;
            const submittedBidder = props.submittedEntity;

            if (originalBidder?.bidderNumber !== submittedBidder.bidderNumber) {
                if (submittedBidder.bidderNumber && submittedBidder.id) {
                    return await props.appContext.confirmations.askForConfirmation({
                        content: {
                            header: `Are you sure you want to changed the bidder number for 
                                "${submittedBidder.contact!.firstName} ${submittedBidder.contact!.lastName}" from "${originalBidder?.bidderNumber ?? ''}" to "${submittedBidder.bidderNumber}"?`,
                            details: 'All lots purchased by this bidder will be updated accordingly',
                            width: '500px',
                        }                        
                    });
                }
            }

            return true;
        },
        action: async props => {
            return await updatedBidderNumberSubmitRequest.post({
                path: 'Bidder',
                postedData: {
                    id: props.submittedEntity.id,
                    bidderNumber: props.submittedEntity.bidderNumber,
                    contact: props.submittedEntity.contact
                },
                convert: updatedBidder => ({
                    ...props.submittedEntity,
                    ...updatedBidder
                })
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id },
                update: bidders => {
                    return BidderDataGetSaleBiddersQuery.actions.writeBidder(bidders, props.responseData);
                }
            });

            props.appContext.popups.closeAll();
        }
    }
}

export const useBidderDataRemoveBidderAction = (): AgTechDataAction<{
    bidderId: number,
    bidderNumber?: string
}, string> => {
    const currentSale = useCurrentSale();
    const popupContext = useAgTechPopUpContext();

    const getLotsQuery = useAgTechQuery({
        query: LotDataGetLotsQuery,
        queryContext: { saleId: currentSale.id },
        projection: lots => Object.values(lots)
    });

    const removeBidderPostRequest = useAgTechHttpPostRequest<number, any>();
    
    return {
        name: 'DeleteBidder',
        getConfiguration: () => ({
            actionExecutionMessage: 'Removing bidder...',
            actionConfirmationMessage: `Successfully removed bidder from sale`
        }),
        validate: async props  => {
            const lots = await waitOnQuery(getLotsQuery);
            const lotsAssociatedWithBidder = lots.filter(lt => lt.originalBidderId === props.submittedEntity.bidderId);
            
            if (lotsAssociatedWithBidder && lotsAssociatedWithBidder.length > 0) {
                let invoicedLotsAssociatedWithBidder = lotsAssociatedWithBidder.filter(lot => isLotInvoiced(lot));

                if (invoicedLotsAssociatedWithBidder && invoicedLotsAssociatedWithBidder.length > 0) {
                    props.validation.failWithWarning(`Bidder ${props.submittedEntity.bidderNumber} has ${invoicedLotsAssociatedWithBidder.length} invoiced lots and cannot be removed from this sale.`);
                }
                else {
                    props.validation.failWithWarning(
                        `Bidder "${props.submittedEntity.bidderNumber ?? 'N/A'}" has purchased ${lotsAssociatedWithBidder.length === 1 ? 'lot ' + lotsAssociatedWithBidder[0].lotNumber + '' : lotsAssociatedWithBidder.length + ' lots'}
                         in this sale. To remove this bidder, please remove bidder information from ${lotsAssociatedWithBidder.length === 1 ? 'that lot' : 'those lots'} first`);
                }
            }
        },
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: `Are you sure you want to remove bidder "${props.submittedEntity?.bidderNumber ?? ''}" from the current sale?`,
                    confirmationButtonText: 'Remove Bidder',
                    width: '400px',
                },
                submission: {
                    actionExecutionMessage: 'Removing bidder...',
                    actionConfirmationMessage: 'Bidder Removed'
                }
            });
        },
        action: async props => {
            return await removeBidderPostRequest.post({
                path: `Bidder/Remove/${currentSale.id}/${props.submittedEntity.bidderId}`,
                postedData: props.submittedEntity.bidderId,
            });
        },
        onSuccess: async props => {
            popupContext.closePopUp();

            props.updateQueryData({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id },
                update: bidders => {
                    return BidderDataGetSaleBiddersQuery.actions.deleteBidder(
                        bidders,
                        props.submittedEntity.bidderId);
                }
            });
        }
    }
}